import React, { useEffect } from 'react'
import { Col, Form, LockIcon, SecondaryLink, color, spacer } from 'stylist'
import yup from 'yup'
import styled, { css } from 'react-emotion'
import SSOProviderButtons from 'components/SSOProviderButtons'
import FormLinks from 'components/FormLinks'
import { findPlatform } from 'config/platforms'
import { yupToFormErrors } from 'formik'
import { isFunction } from 'utils'
import { getSSOForEmailInput } from 'utils/sso'

const StyledFormLinks = styled(FormLinks)`
  justify-content: left !important;
  margin-top: ${spacer(1)};
`

const GreyedFormGroup = styled(Form.Group)`
  ${({ cookiesAccepted, matchedSsoProvider }) =>
    cookiesAccepted &&
    matchedSsoProvider &&
    css`
      opacity: 0.5;
      filter: grayscale(100%);
      pointer-events: none;
    `};
`

const SSOFormGroup = styled(Form.Group)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  color: ${color('cadet')};

  span {
    margin-left: ${spacer(2)};
  }
`

const ActionFormGroup = styled(Form.Group)`
  > div {
    margin-top: 0;
  }
`

const cookieError = 'Cookie policy must be accepted'
const schema = yup.object().shape({
  email: yup
    .string()
    .ensure()
    .email('Email must be a valid email address')
    .required("Email can't be blank"),
  password: yup.string().required("Password can't be blank"),
  cookiesAccepted: yup
    .boolean()
    .test('is-accepted', cookieError, value => value)
})

const LoginForm = ({
  query,
  notify,
  matchedSsoProvider,
  setState,
  authenticate,
  cookiesAccepted,
  setCookieAcceptance,
  highlightAcceptCookiesButton
}) => {
  const platform = findPlatform(query.get('client_id')) || {}
  let mounted = true
  useEffect(
    () => () => {
      mounted = false
    },
    []
  )
  const checkSSOMatch = value =>
    getSSOForEmailInput(
      value,
      match => mounted && setState({ matchedSsoProvider: match })
    )

  return (
    <Form
      initialValues={{
        email: query.get('email') || '',
        password: ''
      }}
      validate={(values, props) => {
        const allValues = { ...values, cookiesAccepted }

        return schema
          .validate(allValues, { abortEarly: false })
          .then(() => {
            return {}
          })
          .catch(error => {
            error.errors.find(a => a === cookieError) &&
              highlightAcceptCookiesButton(true)
            throw yupToFormErrors(error)
          })
      }}
      onSubmit={({ email, password }) =>
        authenticate('oauth2-password-grant', {
          email,
          password
        }).catch(({ payload: { error } }) => {
          if (error === 'invalid_grant') {
            return Promise.reject('Email or password is incorrect')
          } else if (error === 'unconfirmed_user') {
            notify('info', 'Account confirmation required')
          } else if (error === 'password_expired') {
            notify('info', 'Your password is expired, please reset.')
          }
        })
      }
    >
      <Col>
        <Form.Errors />
        <Form.Group>
          <label htmlFor="email">Email</label>
          <Form.Input
            id="email"
            type="email"
            name="email"
            autoComplete="off"
            onBlur={() =>
              setState({
                redirectToSsoProvider: Boolean(matchedSsoProvider)
              })
            }
            onChange={e => checkSSOMatch(e.target.value)}
          />
        </Form.Group>
        {matchedSsoProvider ? (
          <SSOFormGroup>
            <LockIcon size=".8rem" />
            <span>Single sign-on enabled</span>
          </SSOFormGroup>
        ) : (
          <Form.Group>
            <label htmlFor="password">Password</label>
            <Form.Input
              id="password"
              type="password"
              name="password"
              autoComplete="off"
            />
            <StyledFormLinks>
              {platform.ssoSignUp && isFunction(platform.signupUrl) ? (
                <SecondaryLink to={platform.signupUrl(window.location.search)}>
                  Sign Up
                </SecondaryLink>
              ) : (
                platform.signupUrl && (
                  <SecondaryLink to={platform.signupUrl}>Sign Up</SecondaryLink>
                )
              )}
              <SecondaryLink to="/login/forgot-password">
                Forgot Password
              </SecondaryLink>
            </StyledFormLinks>
          </Form.Group>
        )}
        <ActionFormGroup>
          <Form.Errors name="cookiesAccepted" />
          <Form.SubmitButton block={true}>
            {matchedSsoProvider ? 'Continue' : 'Sign In'}
          </Form.SubmitButton>
        </ActionFormGroup>
        <GreyedFormGroup
          matchedSsoProvider={matchedSsoProvider}
          cookiesAccepted={cookiesAccepted}
        >
          <SSOProviderButtons
            cookiesAccepted={cookiesAccepted}
            cookieError={cookieError}
          />
        </GreyedFormGroup>
      </Col>
    </Form>
  )
}

export default LoginForm



// WEBPACK FOOTER //
// ./src/routes/login/components/LoginForm.js